/**
 * Reaching the client through the relay.
 *
 * The browser used to call `http://127.0.0.1` directly, which only worked when the person
 * was sitting at the machine running the client. Now the client dials out to the relay and
 * waits, and the browser talks to the same relay (`DEC-10`) — so this works from a phone,
 * from another machine, and from behind any NAT.
 *
 * **What that costs, stated where somebody changing this file will read it:** content now
 * crosses our infrastructure. The relay is written so it cannot read what it carries, but
 * "cannot" is only true once frames are end-to-end encrypted, and **they are not yet**.
 * Until they are, this transport is plaintext through our servers, which is precisely what
 * `DEC-2` promised it would not be. Do not build features on top of this that assume the
 * promise already holds.
 */

export const RELAY_BASE =
  import.meta.env.VITE_RELAY ?? "wss://suite.faion.net/relay"

export interface ClientInfo {
  ok: boolean
  repo?: string
  documents?: number
}

export interface DocList {
  repo: string
  documents: string[]
}

/** `docs.index` — the same documents, with the title, lane and languages they exist in. */
export interface DocIndex {
  repo: string
  /** In order; the first is the language everything else is a translation of. */
  languages: string[]
  /** The sections this repository declares. A hidden one files documents and has no row. */
  lanes: Array<{ id: string; label: string; hidden: boolean }>
  documents: Array<{
    path: string
    title: string
    lane: string | null
    kind: string
    folder: string
    summary?: string
    meta?: Record<string, string>
    variants: Record<string, { path: string; title: string }>
    missing: string[]
  }>
}

/** One repository this client serves. Switching between them is a field, not a reconnect. */
export interface Project { id: string; name: string }

/** A card on a board, with what it has and what the next column would ask of it. */
export interface BoardItem {
  path: string
  slug: string
  title: string
  summary: string
  kind: string
  state: string
  parent: string | null
  meta: Record<string, string>
  artifacts: string[]
  /** Artifacts missing for the next column — computed by the client, never re-derived here. */
  needs: string[]
  next: string | null
}

export interface BoardView {
  path: string
  board: { template: string; title: string; kinds: string[]; states: string[] }
  items: BoardItem[]
  /** What each artifact is for, so a refusal can say more than a filename. */
  artifacts: Record<string, string>
}

/** What the `+` may offer. The client owns this list; the surface only renders it. */
export interface Kind { id: string; title: string; hint: string }

/** Every op answers in its payload, so a refusal arrives as a value, not a rejection. */
export interface OpError { error: string; detail?: string }
export function failed(v: unknown): v is OpError {
  return !!v && typeof v === "object" && typeof (v as OpError).error === "string"
}

export interface Doc {
  path: string
  text: string
  truncated: boolean
  bytes: number
}

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void }

/**
 * One connection to the relay, with request/response over it — **and it comes back.**
 *
 * The client has always retried; the browser did not, and the difference showed. A relay
 * restart (every deploy is one) closed the socket, the page wrote "No client is connected"
 * and stayed that way until somebody reloaded — while the client sat connected on the other
 * side, saying so to nobody. A surface that reports a healthy system as broken is worse
 * than one that reports nothing.
 *
 * Backoff with jitter, for the same reason the client has it: every browser that lost the
 * same relay coming back at the same instant is a second outage caused by the first.
 */
export class Relay {
  private ws: WebSocket | null = null
  private pending = new Map<number, Pending>()
  private seq = 1
  private online = false
  private closed = false
  private attempt = 0
  private timer: number | undefined
  onPresence: (clientOnline: boolean) => void = () => {}

  constructor(private token: string) {}

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(
        `${RELAY_BASE}/ws/browser?token=${encodeURIComponent(this.token)}`)
      this.ws = ws
      ws.onmessage = e => this.onFrame(String(e.data))
      ws.onerror = () => reject(new Error("relay unreachable"))
      ws.onclose = () => {
        this.online = false
        this.onPresence(false)
        // Every in-flight request fails now rather than hanging: a spinner that never
        // resolves is indistinguishable from a broken product.
        for (const p of this.pending.values()) p.reject(new Error("relay disconnected"))
        this.pending.clear()
        this.retry()
      }
      ws.onopen = () => { this.attempt = 0; resolve() }
    })
  }

  private retry() {
    if (this.closed) return
    this.attempt = Math.min(this.attempt + 1, 6)
    const delay = Math.min(30000, 500 * 2 ** this.attempt) * (0.5 + Math.random())
    this.timer = window.setTimeout(() => {
      // A failed attempt closes, which schedules the next one — so nothing is needed here
      // beyond swallowing the rejection this promise makes.
      this.connect().catch(() => {})
    }, delay)
  }

  private onFrame(raw: string) {
    let m: Record<string, unknown>
    try { m = JSON.parse(raw) } catch { return }

    if (m.kind === "ready" || m.kind === "presence") {
      this.online = Boolean(m.client_online)
      this.onPresence(this.online)
      return
    }
    const id = m.id as number | undefined
    if (id === undefined) return
    const p = this.pending.get(id)
    if (!p) return
    this.pending.delete(id)
    if (m.kind === "error") p.reject(new Error(String(m.error)))
    else p.resolve(m.payload)
  }

  clientOnline() { return this.online }

  /** Ask the client to do one thing. Rejects if no client is connected. */
  call<T>(op: string, payload?: unknown, timeoutMs = 15000): Promise<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error("not connected to the relay"))
    }
    const id = this.seq++
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id)
        reject(new Error(`${op} timed out`))
      }, timeoutMs)
      this.pending.set(id, {
        resolve: v => { clearTimeout(timer); resolve(v as T) },
        reject: e => { clearTimeout(timer); reject(e) },
      })
      this.ws!.send(JSON.stringify({ id, op, payload }))
    })
  }

  close() {
    this.closed = true
    window.clearTimeout(this.timer)
    this.ws?.close()
  }
}

/**
 * Every call names its project.
 *
 * The client could have remembered which one the browser last asked about, and then two
 * tabs open on two projects would race — the loser served the other's documents under its
 * own name, with nothing on screen to say so.
 */
type Written = { ok?: boolean; path?: string } & Partial<OpError>

export const listProjects = (r: Relay) => r.call<{ projects: Project[] }>("projects.list")
export const listDocs = (r: Relay, project?: string) =>
  r.call<DocList>("docs.list", { project })
export const indexDocs = (r: Relay, project?: string) =>
  r.call<DocIndex>("docs.index", { project })
export const listKinds = (r: Relay, project: string | undefined, parent: string) =>
  r.call<{ kinds: Kind[] }>("docs.kinds", { project, parent })
export const createDoc = (r: Relay, req: {
  project?: string; kind: string; name: string
  lane?: string | null; parent?: string; item_parent?: string | null
}) => r.call<Written>("docs.create", req)
export const readDoc = (r: Relay, path: string, project?: string) =>
  r.call<Doc>("docs.read", { path, project })

export const getBoard = (r: Relay, path: string, project?: string) =>
  r.call<BoardView>("board.get", { path, project })
export const moveItem = (r: Relay, path: string, state: string, project?: string) =>
  r.call<Written>("board.move", { path, state, project })
export const promoteIdea = (
  r: Relay, path: string, board: string, kind: string, project?: string,
) => r.call<Written>("board.promote", { path, board, kind, project })
